import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { config } from '@/app/config/env';
import { baseApi } from '@/services/api/baseApi';

import { rootReducer, type RootState } from './rootReducer';

/**
 * Options shared by both construction paths.
 *
 * `immutableCheck` and `serializableCheck` catch accidental state mutation and
 * non-serializable values, which silently break referential-equality checks
 * and therefore rerender correctness. Both are dev-only in RTK.
 */
const MIDDLEWARE_OPTIONS = {
  immutableCheck: config.isDevelopment,
  serializableCheck: config.isDevelopment,
} as const;

/**
 * Builds a store with no seeded state.
 *
 * Kept as its own function because RTK infers the middleware tuple correctly
 * only when `preloadedState` is absent from the options object. `AppStore` is
 * derived from this call, so the exported type is the accurate one.
 */
function createStoreInstance() {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware(MIDDLEWARE_OPTIONS).concat(baseApi.middleware),
    // Redux DevTools expose full application state, including anything a
    // future slice might hold. Never enabled in a production build.
    devTools: config.features.devtools,
  });
}

export type AppStore = ReturnType<typeof createStoreInstance>;
export type AppDispatch = AppStore['dispatch'];
export type { RootState };

/**
 * Builds a store instance.
 *
 * Exposed as a factory so tests can create an isolated store — sharing one
 * module-level store across test cases leaks state between them.
 *
 * @param preloadedState Partial state for tests and future SSR hydration.
 */
export function makeStore(preloadedState?: Partial<RootState>): AppStore {
  let store: AppStore;

  if (preloadedState) {
    /*
     * RTK cannot infer the middleware tuple when `preloadedState` is present
     * under `exactOptionalPropertyTypes`: the generic collapses to the default
     * middleware list and rejects the concatenated API middleware. The cast is
     * confined to this one call and the result is re-typed as `AppStore` —
     * derived from the clean call above — so dispatch stays fully typed.
     */
    store = configureStore({
      reducer: rootReducer,
      preloadedState,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware(MIDDLEWARE_OPTIONS).concat(baseApi.middleware),
      devTools: config.features.devtools,
    } as Parameters<typeof configureStore>[0]) as AppStore;
  } else {
    store = createStoreInstance();
  }

  // Wires up refetchOnFocus / refetchOnReconnect.
  setupListeners(store.dispatch);

  return store;
}

export const store = makeStore();
